import React from 'react'
import { ArrowRight, List, Mic } from 'lucide-react'
import { cn } from '@kiao-voice/ui-kit'

/**
 * 「你說的話 → 整理後的字」這個左右對照版型。語音輸入頁用兩次：一次是唸給你看的
 * 例子，一次是可以按的示範卡。兩個框的外框內距、欄距、箭頭、小標、灰底欄位完全
 * 一樣——讀者剛看懂例子，下一塊長得一模一樣，就知道那是換他做。
 *
 * 所以尺寸住在這裡而不是各自抄一份：抄一份的結果是其中一個框改了、另一個沒改，
 * 而「一模一樣」正是這個版型唯一在傳達的事。
 */

/** 外框：只有髮絲線，不填底色。 */
export const PAIR_FRAME_CLASS = 'rounded-(--radius) border border-border p-(--inset-product-frame)'

/** 左欄｜箭頭｜右欄。箭頭兩側永遠等距；≤720px 收成單欄。 */
export const PAIR_GRID_CLASS =
  'grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-(--gap-lg) gap-y-[18px] max-[720px]:grid-cols-1'

/** 說出口的那段話。 */
export const PAIR_SPEECH_CLASS =
  'text-xl leading-[1.8] [font-variant-numeric:lining-nums] max-[720px]:text-lg'

/**
 * 整理後的字所在的淡灰「輸入框」：右緣撐到離外框線 8px，跟外框對齊成一體，
 * 所以箭頭到左欄文字、箭頭到灰框的距離相同（手機兩側都撐到離外框 8px）。
 */
export const PAIR_FIELD_CLASS = cn(
  'rounded-(--radius-sm) bg-paper-field py-3.5 pl-4',
  'mr-[calc(8px-var(--inset-product-frame))] pr-[calc(var(--inset-product-frame)-8px)]',
  'max-[720px]:mx-[calc(8px-var(--inset-product-frame))] max-[720px]:px-[calc(var(--inset-product-frame)-8px)]'
)

export interface PairLabelProps {
  children: React.ReactNode
  /** 右欄（整理後）的小標。圖示圈換成品牌綠實心。 */
  done?: boolean
  className?: string
}

/**
 * 欄位小標。兩段的標題要一眼分得出「你說的」與「整理後的」，所以是圖示圈＋粗體字，
 * 不是小字 eyebrow。
 */
export function PairLabel({ children, done = false, className }: PairLabelProps) {
  const Icon = done ? List : Mic
  return (
    <p className={cn('mb-4 flex items-center gap-2.5 text-lg leading-[1.5] font-semibold', className)}>
      <span
        aria-hidden="true"
        className={cn(
          'grid size-[30px] flex-none place-items-center rounded-full border',
          // 綠在設計系統裡的意思是「事情已經成立」，正好是整理完成的狀態；只用在
          // 30px 的小圖示圈，不擴大成色塊。紅在這一區已經代表「被改動的地方」
          // （底線），所以不用紅，避免兩種意思撞在一起。
          done ? 'border-ok bg-ok text-background' : 'border-foreground text-foreground'
        )}
      >
        <Icon aria-hidden="true" className="size-4" strokeWidth={1.8} />
      </span>
      {children}
    </p>
  )
}

/** 中間的箭頭。桌機橫向；手機轉成向下，並左右接一條髮絲線當分隔。 */
export function PairArrow() {
  return (
    <div
      aria-hidden="true"
      className="flex items-center justify-center gap-3.5 self-center max-[720px]:w-full"
    >
      <span className="hidden h-px flex-1 bg-line-soft max-[720px]:block" />
      <span className="grid size-11 flex-none place-items-center rounded-full bg-foreground text-background max-[720px]:size-10">
        <ArrowRight aria-hidden="true" className="size-5 max-[720px]:rotate-90" />
      </span>
      <span className="hidden h-px flex-1 bg-line-soft max-[720px]:block" />
    </div>
  )
}
